import { addDays } from "@/lib/dates";
import { monthLabel, monthOf, monthRange, type Calendar } from "@/lib/calendar";
import type { FiscalYear } from "@/api/types";

export type GlPeriod = "month" | "last" | "d30" | "fytd";

/**
 * The dates a shortcut means. Each stays inside the fiscal year it starts from, because the ledger's
 * opening rolls forward from that year's first day.
 */
export function rangeFor(
  period: GlPeriod,
  today: string,
  year: Pick<FiscalYear, "start"> | undefined,
  calendar: Calendar = "gregorian",
): { from: string; to: string } {
  const monthStart = (iso: string) => monthRange(monthOf(iso, calendar)).from;
  const start = year?.start ?? monthStart(today);
  const atLeastStart = (iso: string) => (iso < start ? start : iso);
  switch (period) {
    case "month":
      return { from: atLeastStart(monthStart(today)), to: today };
    case "last": {
      const lastDay = addDays(monthStart(today), -1);
      return { from: atLeastStart(monthStart(lastDay)), to: lastDay };
    }
    case "d30":
      return { from: atLeastStart(addDays(today, -30)), to: today };
    case "fytd":
      return { from: start, to: today };
  }
}

export interface DateShortcut {
  id: GlPeriod;
  label: string;
  from: string;
  to: string;
}

/** "Bhadra" from "Bhadra 2083", and "Aug" from "Aug 2026": the year is in the dates beside it. */
const monthName = (iso: string, calendar: Calendar) =>
  monthLabel(monthOf(iso, calendar)).split(" ")[0] ?? "";

/** What the From and To field offers. Typing dates is the custom range, so there's no such option. */
export function dateShortcuts(
  today: string,
  year: Pick<FiscalYear, "start"> | undefined,
  calendar: Calendar = "gregorian",
): DateShortcut[] {
  const last = rangeFor("last", today, year, calendar);
  return [
    {
      id: "month",
      label: `This month · ${monthName(today, calendar)} to date`,
      ...rangeFor("month", today, year, calendar),
    },
    { id: "last", label: `Last month · ${monthName(last.to, calendar)}`, ...last },
    { id: "d30", label: "Last 30 days", ...rangeFor("d30", today, year, calendar) },
    { id: "fytd", label: "Fiscal year to date", ...rangeFor("fytd", today, year, calendar) },
  ];
}
